import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateCuisineRequestSchema,
  type CreateCuisineRequest,
  type CuisineDetail,
  type CuisineListItem,
  type UpdateCuisineRequest,
  UpdateCuisineRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateCuisine, useUpdateCuisine } from '../use-cuisines';

interface CuisineFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  cuisine?: CuisineDetail | CuisineListItem | null;
}

const CREATE_DEFAULTS: CreateCuisineRequest = {
  code: '',
  name: '',
  region: null,
  isActive: true,
  sortOrder: 0,
};

const Fields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="code">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value.toLowerCase())}
          label="Slug-style code"
          placeholder="e.g. north-indian, italian"
          hint="Lowercase + dashes; unique"
          maxLength={40}
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Display name"
          placeholder="e.g. North Indian, Italian"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="region">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Region"
          placeholder="e.g. South Asian, European, Mediterranean"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? 0 : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive cuisines are hidden from pickers."
          checked={field.value !== false}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const CuisineFormDrawer = ({
  open,
  onOpenChange,
  mode,
  cuisine,
}: CuisineFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createCuisine = useCreateCuisine();
  const updateCuisine = useUpdateCuisine();
  const pending = createCuisine.isPending || updateCuisine.isPending;

  const createForm = useForm<CreateCuisineRequest>({
    resolver: zodResolver(CreateCuisineRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateCuisineRequest>({
    resolver: zodResolver(UpdateCuisineRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) createForm.reset(CREATE_DEFAULTS);
    else if (cuisine)
      updateForm.reset({
        code: cuisine.code,
        name: cuisine.name,
        region: cuisine.region,
        isActive: cuisine.isActive,
        sortOrder: cuisine.sortOrder,
      });
  }, [open, isCreate, cuisine, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createCuisine.mutateAsync({ ...values, region: values.region?.trim() || null });
    onOpenChange(false);
  });
  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!cuisine) return;
    await updateCuisine.mutateAsync({
      id: cuisine.id,
      payload: { ...values, region: values.region?.trim() || null },
    });
    onOpenChange(false);
  });

  const fields = <Fields pending={pending} />;
  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add cuisine' : `Edit ${cuisine?.name ?? 'cuisine'}`}
      description="Used by the MealService picker."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
